<template>
    <vx-expandable-card>
        <template v-slot:header> Details </template>
        <template v-slot:content>
            <div class="flex flex-col gap-4">
                <div>
                    <vx-label size="md">External ID</vx-label>

                    <vx-input
                        data-test="group-id-input"
                        autofocus
                        v-model="form.group_id"
                        :disabled="props.lockedGroupId"
                        size="lg"
                        placeholder="Enter external ID"
                        :error="displayFirstError(validate.group_id.$errors)"
                    >
                        <template v-if="props.lockedGroupId" v-slot:left>
                            <font-awesome-icon widthAuto size="sm" :icon="faLock" class="mx-2"></font-awesome-icon>
                        </template>
                    </vx-input>
                </div>

                <div>
                    <vx-label size="md">Name</vx-label>

                    <vx-input
                        data-test="name-input"
                        v-model="form.name"
                        size="lg"
                        placeholder="Enter group name"
                        :error="displayFirstError(validate.name.$errors)"
                    ></vx-input>
                </div>

                <div v-if="!prefillTeam">
                    <vx-label size="md">Team</vx-label>

                    <vx-selectable
                        data-test="team-select"
                        v-model="form.team_id"
                        :options="teams"
                        @search="search"
                        size="lg"
                        label="name"
                        :reduce="(option) => option.id"
                        :clearable="false"
                        placeholder="Select a team"
                        :error="displayFirstError(validate.team_id.$errors)"
                    ></vx-selectable>
                </div>

                <div>
                    <vx-label size="md">HubSpot Company ID </vx-label>

                    <hubspot-company-id-input
                        v-model="form.hubspot_company_id"
                        placeholder="HubSpot Company ID"
                        size="lg"
                        :error="displayFirstError($externalResults.hubspot_company_id)"
                    />
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxExpandableCard, VxInput, VxLabel, VxSelectable } from '@voxie/frontend-components';
import HubspotCompanyIdInput from '~/components/general/HubspotCompanyIdInput.vue';
import { ref, computed } from 'vue';
import { adminTeamsClient } from '~/services';
import { displayFirstError, groupAndOrgUnitName } from '~/utils/validation';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import debounce from '~/utils/debounce';
import { maxLength, required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { useUrlSearchParams } from '@vueuse/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faLock } from '@fortawesome/pro-solid-svg-icons';

const { $externalResults } = useFormRequestHandler();

const queryParams = useUrlSearchParams();
const prefillTeam = !!queryParams.team_id;

const props = defineProps({
    lockedGroupId: {
        type: Boolean,
        default: false,
    },
});

const form = defineModel({
    required: true,
    type: Object,
});

const teams = ref([]);

const search = debounce((query) => {
    adminTeamsClient
        .getTeams({
            query,
            status: 'active',
            has_feature: 'contact_groups',
        })
        .then(
            (res) =>
                (teams.value = res.data.data.map((team) => ({
                    ...team,
                    name: `${team.id} - ${team.name}`,
                })))
        );
});

search();

const validationRules = computed(() => ({
    group_id: {
        required,
        maxLength: maxLength(255),
    },
    name: {
        required,
        maxLength: maxLength(80),
        format: groupAndOrgUnitName,
    },
    team_id: {
        required,
    },
    hubspot_company_id: {},
}));

const validate = useVuelidate(validationRules, form);
</script>
